/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import type { CustomerTask } from '../screens/hooks/useCustomerTasks';

type TaskKind = 'call' | 'message' | 'visit' | 'task';

type Props = {
  tasks: CustomerTask[];
  loading: boolean;
  canEdit: boolean;
  page?: number;
  hasMore?: boolean;
  onLoadPage?: (page: number) => void;
  onAddTask: (title: string, kind: TaskKind, dueDate: string | null) => Promise<void>;
  onCompleteTask: (taskId: string) => void;
  onArchiveTask: (taskId: string) => void;
  mobile?: boolean;
};

const KIND_ICON: Record<TaskKind, keyof typeof Ionicons.glyphMap> = {
  call: 'call-outline',
  message: 'chatbubble-outline',
  visit: 'footsteps-outline',
  task: 'checkmark-circle-outline',
};

const KIND_LABEL: Record<TaskKind, string> = {
  call: 'Llamar',
  message: 'Mensaje',
  visit: 'Visita',
  task: 'Tarea',
};

const KINDS: TaskKind[] = ['call', 'message', 'visit', 'task'];

function formatDue(iso: string | null): string {
  if (!iso) return 'Sin fecha';
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const due = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffMs = due.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / 86400000);
  const dateStr = d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
  if (diffDays === 0) return `Hoy`;
  if (diffDays === 1) return `Mañana`;
  if (diffDays === -1) return `Ayer`;
  if (diffDays < 0) return `Venció ${dateStr}`;
  return dateStr;
}

function isOverdue(task: CustomerTask): boolean {
  if (task.status !== 'pending' || !task.dueDate) return false;
  return new Date(task.dueDate).getTime() < Date.now();
}

export function CustomerTasksSection({
  tasks,
  loading,
  canEdit,
  page = 0,
  hasMore = false,
  onLoadPage,
  onAddTask,
  onCompleteTask,
  onArchiveTask,
  mobile = false,
}: Props) {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<TaskKind>('call');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  const pendingCount = tasks.filter((t) => t.status === 'pending').length;

  const handleSave = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      const due = dueDate.trim()
        ? new Date(`${dueDate.trim()}T12:00:00`).toISOString()
        : null;
      await onAddTask(title.trim(), kind, due);
      setTitle('');
      setKind('call');
      setDueDate('');
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PaperSheet
      style={mobile ? styles.panelMobile : styles.panelWide}
      testID="customers.detail.customer-tasks"
    >
      <View style={styles.headerRow}>
        <Ionicons name="checkbox-outline" size={18} color={ticketColors.ink} />
        <Text style={styles.title}>Tareas</Text>
        {pendingCount > 0 ? (
          <View style={styles.countBadge} testID="customers.detail.tasks.count">
            <Text style={styles.countBadgeText}>{pendingCount}</Text>
          </View>
        ) : null}
        {canEdit && !showForm ? (
          <TouchableOpacity
            style={styles.addBtn}
            onPress={() => setShowForm(true)}
            accessibilityRole="button"
            accessibilityLabel="Agregar tarea"
            testID="customers.detail.tasks.add"
          >
            <Ionicons name="add" size={16} color="#B45309" />
            <Text style={styles.addBtnText}>Agregar</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {showForm && canEdit ? (
        <View style={styles.form} testID="customers.detail.tasks.form">
          <TextInput
            style={styles.input}
            value={title}
            onChangeText={setTitle}
            placeholder="Ej: Llamar para reconfirmar el turno"
            placeholderTextColor={ticketColors.ink3}
            testID="customers.detail.tasks.title-input"
          />
          <View style={styles.kindRow}>
            {KINDS.map((k) => (
              <TouchableOpacity
                key={k}
                style={[styles.kindChip, kind === k && styles.kindChipActive]}
                onPress={() => setKind(k)}
                accessibilityRole="button"
                accessibilityLabel={KIND_LABEL[k]}
                testID={`customers.detail.tasks.kind.${k}`}
              >
                <Ionicons
                  name={KIND_ICON[k]}
                  size={14}
                  color={kind === k ? '#fff' : ticketColors.ink2}
                />
                <Text
                  style={[styles.kindText, kind === k && styles.kindTextActive]}
                >
                  {KIND_LABEL[k]}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.dueRow}>
            <Ionicons name="calendar-outline" size={16} color={ticketColors.ink3} />
            <TextInput
              style={styles.dueInput}
              value={dueDate}
              onChangeText={setDueDate}
              placeholder="AAAA-MM-DD (opcional)"
              placeholderTextColor={ticketColors.ink3}
              testID="customers.detail.tasks.due-input"
            />
          </View>
          <View style={styles.formBtns}>
            <TouchableOpacity
              style={styles.cancelBtn}
              onPress={() => {
                setShowForm(false);
                setTitle('');
                setKind('call');
                setDueDate('');
              }}
              disabled={saving}
            >
              <Text style={styles.cancelText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.saveBtn, (!title.trim() || saving) && styles.saveBtnDisabled]}
              onPress={handleSave}
              disabled={!title.trim() || saving}
              testID="customers.detail.tasks.save"
            >
              <Text style={styles.saveText}>{saving ? 'Guardando…' : 'Guardar'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}

      {loading ? (
        <Text style={styles.emptyText}>Cargando tareas…</Text>
      ) : tasks.length === 0 && !showForm ? (
        <Text style={styles.emptyText}>Sin tareas. Agregá un seguimiento.</Text>
      ) : (
        <View style={styles.list}>
          {tasks.map((task) => {
            const overdue = isOverdue(task);
            return (
              <View
                key={task.id}
                style={[styles.taskCard, task.status === 'done' && styles.taskCardDone]}
                testID="customers.detail.task"
              >
                <View style={styles.taskMain}>
                  <Ionicons
                    name={KIND_ICON[task.kind]}
                    size={16}
                    color={task.status === 'done' ? ticketColors.ink3 : ticketColors.ink2}
                  />
                  <View style={styles.taskBody}>
                    <Text
                      style={[
                        styles.taskTitle,
                        task.status === 'done' && styles.taskTitleDone,
                      ]}
                      numberOfLines={2}
                    >
                      {task.title}
                    </Text>
                    <View style={styles.taskMeta}>
                      <Text style={[styles.taskKind]}>{KIND_LABEL[task.kind]}</Text>
                      <Text
                        style={[
                          styles.taskDue,
                          overdue && styles.taskDueOverdue,
                          task.status === 'done' && styles.taskDueDone,
                        ]}
                      >
                        {task.status === 'done' && task.completedAt
                          ? `Hecha ${formatDue(task.completedAt)}`
                          : formatDue(task.dueDate)}
                      </Text>
                    </View>
                  </View>
                </View>
                {canEdit ? (
                  <View style={styles.taskActions}>
                    {task.status === 'pending' ? (
                      <TouchableOpacity
                        onPress={() => onCompleteTask(task.id)}
                        accessibilityRole="button"
                        accessibilityLabel="Marcar como hecha"
                        testID={`customers.detail.task.complete.${task.id}`}
                      >
                        <Ionicons name="checkmark-circle" size={20} color={ticketColors.success} />
                      </TouchableOpacity>
                    ) : null}
                    <TouchableOpacity
                      onPress={() => onArchiveTask(task.id)}
                      accessibilityRole="button"
                      accessibilityLabel="Archivar tarea"
                      testID={`customers.detail.task.archive.${task.id}`}
                    >
                      <Ionicons name="trash-outline" size={16} color={ticketColors.ink3} />
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      )}

      {(hasMore || page > 0) && onLoadPage ? (
        <View style={styles.pagerRow} testID="customers.detail.tasks-pager">
          <TouchableOpacity
            style={[styles.pagerBtn, page === 0 && styles.pagerBtnDisabled]}
            onPress={() => page > 0 && onLoadPage(page - 1)}
            disabled={page === 0 || loading}
            accessibilityRole="button"
            accessibilityLabel="Página anterior"
          >
            <Ionicons name="chevron-back" size={16} color={page === 0 ? ticketColors.ink3 : ticketColors.ink} />
            <Text style={[styles.pagerText, page === 0 && styles.pagerTextDisabled]}>Anterior</Text>
          </TouchableOpacity>
          <Text style={styles.pagerInfo}>Pág. {page + 1}</Text>
          <TouchableOpacity
            style={[styles.pagerBtn, !hasMore && styles.pagerBtnDisabled]}
            onPress={() => hasMore && onLoadPage(page + 1)}
            disabled={!hasMore || loading}
            accessibilityRole="button"
            accessibilityLabel="Página siguiente"
          >
            <Text style={[styles.pagerText, !hasMore && styles.pagerTextDisabled]}>Siguiente</Text>
            <Ionicons name="chevron-forward" size={16} color={!hasMore ? ticketColors.ink3 : ticketColors.ink} />
          </TouchableOpacity>
        </View>
      ) : null}
    </PaperSheet>
  );
}

const styles = StyleSheet.create({
  panelWide: { padding: spacing.md },
  panelMobile: { padding: spacing.sm },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  countBadge: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingHorizontal: 7,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center',
  },
  countBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#fff',
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginLeft: 'auto',
  },
  addBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#B45309',
  },
  form: { marginBottom: spacing.sm },
  input: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    padding: spacing.sm,
    fontSize: 14,
    color: ticketColors.ink,
    marginBottom: spacing.xs,
  },
  kindRow: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: spacing.xs,
    flexWrap: 'wrap',
  },
  kindChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: 'transparent',
  },
  kindChipActive: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  kindText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  kindTextActive: {
    color: '#fff',
  },
  dueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: spacing.xs,
  },
  dueInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: spacing.sm,
    fontSize: 13,
    color: ticketColors.ink,
  },
  formBtns: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
  },
  cancelBtn: { paddingVertical: 6, paddingHorizontal: spacing.sm },
  cancelText: { fontSize: 13, color: ticketColors.ink3, fontWeight: '600' },
  saveBtn: {
    backgroundColor: ticketColors.gold,
    paddingVertical: 6,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
  },
  saveBtnDisabled: { opacity: 0.5 },
  saveText: { fontSize: 13, color: '#fff', fontWeight: '700' },
  list: { gap: 0 },
  taskCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  taskCardDone: { opacity: 0.6 },
  taskMain: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    flex: 1,
  },
  taskBody: { flex: 1 },
  taskTitle: {
    fontSize: 14,
    color: ticketColors.ink,
    marginBottom: 2,
  },
  taskTitleDone: {
    textDecorationLine: 'line-through',
    color: ticketColors.ink3,
  },
  taskMeta: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  taskKind: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontWeight: '500',
  },
  taskDue: {
    fontSize: 11,
    color: ticketColors.ink3,
  },
  taskDueOverdue: {
    color: ticketColors.bad,
    fontWeight: '700',
  },
  taskDueDone: {
    color: ticketColors.success,
  },
  taskActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingLeft: spacing.xs,
  },
  emptyText: {
    fontSize: 14,
    color: ticketColors.ink3,
    paddingVertical: spacing.sm,
  },
  pagerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
    marginTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  pagerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    minHeight: 44,
  },
  pagerBtnDisabled: { opacity: 0.4 },
  pagerText: { fontSize: 12, fontWeight: '600', color: ticketColors.ink },
  pagerTextDisabled: { color: ticketColors.ink3 },
  pagerInfo: { fontSize: 12, color: ticketColors.ink3, fontWeight: '500' },
});
